/**
 * Reusable torn-paper edge. Renders absolutely-positioned SVG strips that
 * overlay the top or bottom edge of a band, filled with the page background
 * color so it "eats" into the band and reads as a rip.
 *
 * The ragged contour is generated procedurally with a seeded PRNG so it's
 * deterministic — identical on server and client, no hydration mismatch.
 *
 * Two SVGs are rendered from the same generator — a small/tablet geometry
 * (480x24) and a desktop geometry (1600x34) — toggled with CSS so the contour
 * never gets horizontally compressed into sharp spikes on narrow screens.
 */

function makeRandom(seed: number) {
  let s = seed;
  return () => ((s = (s * 1103515245 + 12345) % 2147483648) / 2147483648);
}

/**
 * Builds the ragged inner contour as a list of [x, y] points, where y is
 * measured from the "inside" edge of the strip (0 = flush, height = deep).
 * Anchor spacing scales with the viewBox width so both sizes keep a similar
 * segment count and neither gets stretched out of shape.
 */
function buildContour(seed: number, width: number, height: number) {
  const rnd = makeRandom(seed);
  const pts: Array<[number, number]> = [];

  // Coarse contour occupies roughly the middle 50% of the strip height.
  const lo = height * 0.25;
  const range = height * 0.5;

  const baseSpan = width / 26;
  const clamp = (y: number) => Math.min(height - 1, Math.max(1, y));

  let x = 0;
  while (x < width) {
    const coarseY = lo + rnd() * range;
    pts.push([x, clamp(coarseY)]);

    const span = baseSpan * (0.85 + rnd() * 0.4);
    const nextX = Math.min(width, x + span);

    // Fine layer: 1-3 small fiber notches between coarse anchors.
    const notches = 1 + Math.floor(rnd() * 3);
    for (let n = 0; n < notches; n++) {
      const t = (n + 1) / (notches + 1) + (rnd() - 0.5) * 0.18;
      const nx = x + (nextX - x) * Math.min(0.95, Math.max(0.05, t));
      const jitter = (rnd() - 0.5) * 3; // +/- 1.5 units
      pts.push([nx, clamp(coarseY + jitter)]);
    }

    x = nextX;
  }

  pts.push([width, clamp(lo + rnd() * range)]);
  return pts;
}

function buildPath(
  seed: number,
  position: "top" | "bottom",
  width: number,
  height: number
) {
  const pts = buildContour(seed, width, height);
  const toY = (y: number) => (position === "top" ? y : height - y);
  const outer = position === "top" ? -1 : height + 1;

  let d = `M -1 ${outer} L -1 ${toY(pts[0][1]).toFixed(2)}`;

  // Rounded corners: run short quadratic curves through midpoints so every
  // direction change is eased rather than knife-edged.
  d += ` L ${pts[0][0].toFixed(2)} ${toY(pts[0][1]).toFixed(2)}`;
  for (let i = 1; i < pts.length - 1; i++) {
    const [cx, cy] = pts[i];
    const [nx, ny] = pts[i + 1];
    const mx = (cx + nx) / 2;
    const my = (cy + ny) / 2;
    d += ` Q ${cx.toFixed(2)} ${toY(cy).toFixed(2)} ${mx.toFixed(2)} ${toY(my).toFixed(2)}`;
  }
  const last = pts[pts.length - 1];
  d += ` L ${last[0].toFixed(2)} ${toY(last[1]).toFixed(2)}`;
  d += ` L ${width + 1} ${outer} Z`;
  return d;
}

interface TornEdgeProps {
  position: "top" | "bottom";
  /** Color the tear reveals — i.e. the page background behind the band. */
  color: string;
  /** Optional fixed strip height in px; defaults to responsive heights. */
  height?: number;
}

export function TornEdge({ position, color, height }: TornEdgeProps) {
  const seed = position === "top" ? 90210 : 31337;
  const smallD = buildPath(seed, position, 480, 24);
  const largeD = buildPath(seed, position, 1600, 34);

  const base = `pointer-events-none absolute inset-x-0 z-20 w-full ${
    position === "top" ? "top-0" : "bottom-0"
  }`;
  const sizing = height ? "" : " h-[18px] md:h-6 lg:h-[34px]";
  // overflow: visible lets the path's ±1 edge overshoot paint past the
  // viewport instead of being clipped into an antialiased hairline.
  const style = height ? { height, overflow: "visible" as const } : { overflow: "visible" as const };

  return (
    <>
      <svg
        aria-hidden="true"
        focusable="false"
        viewBox="0 0 480 24"
        preserveAspectRatio="none"
        className={`${base} lg:hidden${sizing}`}
        style={style}
      >
        <path d={smallD} fill={color} />
      </svg>
      <svg
        aria-hidden="true"
        focusable="false"
        viewBox="0 0 1600 34"
        preserveAspectRatio="none"
        className={`${base} hidden lg:block${sizing}`}
        style={style}
      >
        <path d={largeD} fill={color} />
      </svg>
    </>
  );
}
